import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const STORAGE_KEY = "mh_admin_token";

function LoginScreen({ onSuccess }) {
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!password) return;
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/admin/login`, { password });
      localStorage.setItem(STORAGE_KEY, data.token);
      toast.success("Welcome.");
      onSuccess(data.token);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Incorrect password.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="min-h-screen flex items-center justify-center px-6"
      style={{ background: "var(--mh-bg)" }}
      data-testid="admin-login-screen"
    >
      <div
        className="w-full max-w-sm"
        style={{
          background: "var(--mh-card)",
          padding: "48px 36px",
          border: "1px solid var(--mh-border)",
        }}
      >
        <div className="text-center">
          <div
            className="font-serif-display"
            style={{ fontSize: 32, fontWeight: 500, letterSpacing: "0.02em" }}
          >
            MH
          </div>
          <div
            className="font-sans-body mt-3"
            style={{
              fontSize: 9.5,
              letterSpacing: "0.46em",
              fontWeight: 500,
            }}
          >
            MAISON HUE
          </div>
        </div>

        <h2
          className="font-serif-display text-center mt-10"
          style={{
            fontSize: 26,
            fontWeight: 500,
            lineHeight: 1.15,
          }}
          data-testid="admin-login-title"
        >
          The <span style={{ fontStyle: "italic" }}>maison</span> archive.
        </h2>
        <p
          className="font-serif-display text-center mt-3"
          style={{
            fontSize: 14,
            fontStyle: "italic",
            color: "var(--mh-muted)",
            lineHeight: 1.55,
          }}
        >
          Private. Founder access only.
        </p>

        <form onSubmit={submit} className="mt-8 flex flex-col gap-4" data-testid="admin-login-form">
          <input
            type="password"
            required
            placeholder="Password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="mh-input"
            autoFocus
            data-testid="admin-password-input"
          />
          <button
            type="submit"
            disabled={loading}
            className="mh-btn-solid w-full mt-2"
            data-testid="admin-login-submit"
          >
            {loading ? "Entering…" : "Enter"}
          </button>
        </form>
      </div>
    </div>
  );
}

function formatDate(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleString("en-GB", {
      day: "numeric",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

function Dashboard({ token, onLogout }) {
  const [data, setData] = useState(null);
  const [orders, setOrders] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("");
  const [tab, setTab] = useState("founders"); // 'founders' | 'orders'
  const [expandedOrder, setExpandedOrder] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [waitlistRes, ordersRes] = await Promise.all([
        axios.get(`${API}/admin/waitlist`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/admin/orders`, { headers: { Authorization: `Bearer ${token}` } }),
      ]);
      setData(waitlistRes.data);
      setOrders(ordersRes.data);
    } catch (err) {
      if (err?.response?.status === 401) {
        toast.error("Session expired.");
        onLogout();
      } else {
        toast.error("Could not load.");
      }
    } finally {
      setLoading(false);
    }
  }, [token, onLogout]);

  useEffect(() => {
    fetchData();
    // Auto-refresh every 20 seconds so new founder signups & shade matches appear
    // without the user needing to hit Refresh.
    const interval = setInterval(fetchData, 20000);
    return () => clearInterval(interval);
  }, [fetchData]);

  const downloadCsv = () => {
    const url = `${API}/admin/export.csv?token=${encodeURIComponent(token)}`;
    window.open(url, "_blank");
  };

  const downloadOrdersCsv = () => {
    const url = `${API}/admin/orders/export.csv?token=${encodeURIComponent(token)}`;
    window.open(url, "_blank");
  };

  const downloadOrderManifest = (email) => {
    const url = `${API}/admin/orders/${encodeURIComponent(email)}/manifest.csv?token=${encodeURIComponent(token)}`;
    window.open(url, "_blank");
  };

  const updateOrderStatus = async (email, status) => {
    try {
      await axios.post(
        `${API}/admin/orders/${encodeURIComponent(email)}/status`,
        { status },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Status updated.");
      fetchData();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not update status.");
    }
  };

  const filtered =
    data?.entries?.filter(
      (e) =>
        !filter ||
        e.name.toLowerCase().includes(filter.toLowerCase()) ||
        e.email.toLowerCase().includes(filter.toLowerCase())
    ) || [];

  return (
    <div
      className="min-h-screen"
      style={{ background: "var(--mh-bg)" }}
      data-testid="admin-dashboard"
    >
      {/* Top bar */}
      <header
        className="px-6 md:px-10 lg:px-14 py-6 flex items-center justify-between"
        style={{ borderBottom: "1px solid var(--mh-border-faint)" }}
      >
        <div className="flex flex-col">
          <div
            className="font-serif-display"
            style={{ fontSize: 22, fontWeight: 500, letterSpacing: "0.02em", lineHeight: 1 }}
          >
            MH
          </div>
          <div
            className="font-sans-body mt-2"
            style={{ fontSize: 9, letterSpacing: "0.46em", fontWeight: 500 }}
          >
            MAISON HUE · ARCHIVE
          </div>
        </div>
        <button
          onClick={onLogout}
          className="font-sans-body hover:opacity-60 transition-opacity"
          style={{
            fontSize: 10.5,
            letterSpacing: "0.34em",
            textTransform: "uppercase",
            color: "var(--mh-ink)",
            fontWeight: 500,
            background: "transparent",
            border: "none",
            cursor: "pointer",
          }}
          data-testid="admin-logout"
        >
          Sign out
        </button>
      </header>

      <main className="px-6 md:px-10 lg:px-14 py-12 max-w-6xl mx-auto">
        {/* Page title */}
        <div
          className="font-sans-body mb-4"
          style={{
            fontSize: 10.5,
            letterSpacing: "0.4em",
            textTransform: "uppercase",
            color: "var(--mh-gold-deep)",
            fontWeight: 500,
          }}
        >
          The Founding Circle
        </div>
        <h1
          className="font-serif-display"
          style={{ fontSize: "clamp(36px, 4vw, 56px)", fontWeight: 500, lineHeight: 1, letterSpacing: "-0.01em" }}
          data-testid="admin-page-title"
        >
          Your founders.
        </h1>

        {/* Stat cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mt-12" data-testid="admin-stats">
          <StatCard label="Total signups" value={data?.total ?? "—"} loading={loading} />
          <StatCard label="Founders" value={data?.waitlist_count ?? "—"} loading={loading} />
          <StatCard label="Shade matched" value={data?.shade_matched_count ?? "—"} loading={loading} />
          <StatCard label="Shade reservations" value={data?.shade_reservations_count ?? "—"} loading={loading} />
        </div>

        {/* Tab bar */}
        <div className="flex gap-6 mt-12" style={{ borderBottom: "1px solid var(--mh-border-faint)" }} data-testid="admin-tabs">
          {[
            { id: "founders", label: "Founders" },
            { id: "orders", label: `Orders${orders?.total ? ` · ${orders.total}` : ""}` },
          ].map((t) => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              className="font-sans-body"
              style={{
                fontSize: 11,
                letterSpacing: "0.34em",
                textTransform: "uppercase",
                fontWeight: 500,
                padding: "14px 2px",
                background: "transparent",
                border: "none",
                borderBottom: tab === t.id ? "1.5px solid var(--mh-ink)" : "1.5px solid transparent",
                color: tab === t.id ? "var(--mh-ink)" : "var(--mh-muted)",
                cursor: "pointer",
                marginBottom: -1,
              }}
              data-testid={`admin-tab-${t.id}`}
            >
              {t.label}
            </button>
          ))}
        </div>

        {/* Actions row */}
        <div className="flex flex-col sm:flex-row sm:items-end justify-between mt-10 gap-4">
          <div className="flex-1 max-w-md">
            <div
              className="font-sans-body mb-2"
              style={{
                fontSize: 10,
                letterSpacing: "0.34em",
                textTransform: "uppercase",
                color: "var(--mh-muted)",
                fontWeight: 500,
              }}
            >
              Search
            </div>
            <input
              type="text"
              placeholder={tab === "orders" ? "Customer or shade" : "Name or email"}
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              className="mh-input"
              data-testid="admin-search"
            />
          </div>
          <div className="flex gap-3">
            <button
              onClick={fetchData}
              className="mh-btn-solid"
              data-testid="admin-refresh"
              disabled={loading}
            >
              {loading ? "Refreshing…" : "Refresh"}
            </button>
            <button
              onClick={tab === "orders" ? downloadOrdersCsv : downloadCsv}
              className="mh-btn-solid"
              data-testid="admin-download-csv"
            >
              {tab === "orders" ? "Export Orders" : "Download CSV"}
            </button>
          </div>
        </div>

        {tab === "founders" ? (
          <FoundersTable
            data={data}
            filtered={filtered}
            loading={loading}
          />
        ) : (
          <OrdersTable
            orders={orders}
            filter={filter}
            loading={loading}
            expanded={expandedOrder}
            setExpanded={setExpandedOrder}
            downloadManifest={downloadOrderManifest}
            updateStatus={updateOrderStatus}
          />
        )}
      </main>
    </div>
  );
}

// ===================== Founders Table =====================
function FoundersTable({ data, filtered, loading }) {
  return (
    <div className="mt-8" data-testid="admin-table-wrapper">
      {loading && !data && (
        <div
          className="font-serif-display py-20 text-center"
          style={{ fontStyle: "italic", color: "var(--mh-muted)" }}
        >
          Reading the archive…
        </div>
      )}
      {data && filtered.length === 0 && (
        <div
          className="font-serif-display py-20 text-center"
          style={{ fontStyle: "italic", color: "var(--mh-muted)" }}
        >
          No founders found.
        </div>
      )}
      {filtered.length > 0 && (
        <div style={{ overflowX: "auto" }}>
          <table
            className="w-full"
            style={{ borderCollapse: "collapse", minWidth: 640 }}
            data-testid="admin-table"
          >
            <thead>
              <tr style={{ borderBottom: "1px solid var(--mh-border)" }}>
                {["No.", "Name", "Email", "Source", "Matched shade", "Joined"].map((h) => (
                  <th
                    key={h}
                    className="font-sans-body py-4 px-3 text-left"
                    style={{
                      fontSize: 10,
                      letterSpacing: "0.34em",
                      textTransform: "uppercase",
                      color: "var(--mh-muted)",
                      fontWeight: 500,
                    }}
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.map((e, i) => (
                <tr
                  key={e.id || i}
                  style={{ borderBottom: "1px solid var(--mh-border-faint)" }}
                  data-testid={`admin-row-${i}`}
                >
                  <td
                    className="font-sans-body py-4 px-3"
                    style={{ fontSize: 13, color: "var(--mh-muted)", fontWeight: 400, width: 60 }}
                  >
                    {String(i + 1).padStart(2, "0")}
                  </td>
                  <td className="font-serif-display py-4 px-3" style={{ fontSize: 17, fontWeight: 500 }}>
                    {e.name || "—"}
                  </td>
                  <td
                    className="font-sans-body py-4 px-3"
                    style={{ fontSize: 13, color: "var(--mh-ink-soft)", fontWeight: 400 }}
                  >
                    <a
                      href={`mailto:${e.email}`}
                      style={{ color: "inherit", textDecoration: "none" }}
                      className="hover:underline"
                    >
                      {e.email}
                    </a>
                  </td>
                  <td
                    className="font-sans-body py-4 px-3"
                    style={{
                      fontSize: 10,
                      letterSpacing: "0.28em",
                      textTransform: "uppercase",
                      color:
                        e.source === "reserve_shade"
                          ? "var(--mh-gold-deep)"
                          : e.source === "shade_match"
                          ? "var(--mh-gold-deep)"
                          : "var(--mh-muted)",
                      fontWeight: 500,
                    }}
                  >
                    {e.source === "reserve_shade"
                      ? "Shade"
                      : e.source === "shade_match"
                      ? "Match"
                      : "Founder"}
                  </td>
                  <td
                    className="font-serif-display py-4 px-3"
                    style={{
                      fontSize: 14,
                      fontStyle: e.matched_shade ? "italic" : "normal",
                      color: e.matched_shade ? "var(--mh-ink)" : "var(--mh-muted)",
                      fontWeight: 400,
                    }}
                  >
                    {e.matched_shade ? (
                      <span style={{ display: "inline-flex", alignItems: "center", gap: 10 }}>
                        {e.hex && (
                          <span
                            style={{
                              display: "inline-block",
                              width: 14,
                              height: 14,
                              borderRadius: "50%",
                              background: e.hex,
                              border: "0.5px solid var(--mh-border)",
                            }}
                          />
                        )}
                        <span>
                          {e.matched_shade}
                          {e.formula_num ? (
                            <span
                              style={{
                                fontFamily: "inherit",
                                fontStyle: "normal",
                                fontSize: 10,
                                letterSpacing: "0.24em",
                                marginLeft: 8,
                                color: "var(--mh-muted)",
                              }}
                            >
                              N° {e.formula_num}
                            </span>
                          ) : null}
                        </span>
                      </span>
                    ) : (
                      "—"
                    )}
                  </td>
                  <td
                    className="font-sans-body py-4 px-3"
                    style={{ fontSize: 12.5, color: "var(--mh-muted)", fontWeight: 400 }}
                  >
                    {formatDate(e.created_at)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ===================== Orders Table =====================
const STATUS_OPTIONS = [
  { id: "pending", label: "Pending" },
  { id: "in_production", label: "In production" },
  { id: "produced", label: "Produced" },
  { id: "shipped", label: "Shipped" },
];

function statusColor(s) {
  switch (s) {
    case "in_production": return "var(--mh-gold-deep)";
    case "produced": return "#3a7d3a";
    case "shipped": return "var(--mh-ink)";
    default: return "var(--mh-muted)";
  }
}

function OrdersTable({ orders, filter, loading, expanded, setExpanded, downloadManifest, updateStatus }) {
  if (loading && !orders) {
    return (
      <div
        className="font-serif-display py-20 text-center"
        style={{ fontStyle: "italic", color: "var(--mh-muted)" }}
      >
        Loading orders…
      </div>
    );
  }
  const items = (orders?.orders || []).filter((o) => {
    if (!filter) return true;
    const f = filter.toLowerCase();
    return (
      (o.name || "").toLowerCase().includes(f) ||
      (o.email || "").toLowerCase().includes(f) ||
      (o.matched_shade || "").toLowerCase().includes(f)
    );
  });
  if (items.length === 0) {
    return (
      <div
        className="font-serif-display py-20 text-center"
        style={{ fontStyle: "italic", color: "var(--mh-muted)" }}
      >
        No orders yet.
      </div>
    );
  }
  return (
    <div className="mt-8" data-testid="admin-orders-wrapper">
      <div style={{ overflowX: "auto" }}>
        <table
          className="w-full"
          style={{ borderCollapse: "collapse", minWidth: 720 }}
          data-testid="admin-orders-table"
        >
          <thead>
            <tr style={{ borderBottom: "1px solid var(--mh-border)" }}>
              {["No.", "Customer", "Shade", "Status", "Ordered", "Manifest"].map((h) => (
                <th
                  key={h}
                  className="font-sans-body py-4 px-3 text-left"
                  style={{
                    fontSize: 10,
                    letterSpacing: "0.34em",
                    textTransform: "uppercase",
                    color: "var(--mh-muted)",
                    fontWeight: 500,
                  }}
                >
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {items.map((o, i) => {
              const isOpen = expanded === o.email;
              return (
                <React.Fragment key={o.email}>
                  <tr
                    style={{ borderBottom: isOpen ? "none" : "1px solid var(--mh-border-faint)", cursor: "pointer" }}
                    onClick={() => setExpanded(isOpen ? null : o.email)}
                    data-testid={`admin-order-row-${i}`}
                  >
                    <td className="font-sans-body py-4 px-3" style={{ fontSize: 13, color: "var(--mh-muted)", width: 60 }}>
                      {String(i + 1).padStart(2, "0")}
                    </td>
                    <td className="py-4 px-3">
                      <div className="font-serif-display" style={{ fontSize: 16, fontWeight: 500 }}>
                        {o.name || "—"}
                      </div>
                      <div className="font-sans-body" style={{ fontSize: 12, color: "var(--mh-ink-soft)" }}>
                        {o.email}
                      </div>
                    </td>
                    <td className="font-serif-display py-4 px-3" style={{ fontSize: 14, fontStyle: "italic" }}>
                      <span style={{ display: "inline-flex", alignItems: "center", gap: 10 }}>
                        {o.hex && (
                          <span
                            style={{
                              display: "inline-block",
                              width: 14,
                              height: 14,
                              borderRadius: "50%",
                              background: o.hex,
                              border: "0.5px solid var(--mh-border)",
                            }}
                          />
                        )}
                        <span>
                          {o.matched_shade}
                          {o.formula_num ? (
                            <span style={{ fontStyle: "normal", fontSize: 10, letterSpacing: "0.24em", marginLeft: 8, color: "var(--mh-muted)" }}>
                              N° {o.formula_num}
                            </span>
                          ) : null}
                        </span>
                      </span>
                    </td>
                    <td className="font-sans-body py-4 px-3" style={{ fontSize: 10, letterSpacing: "0.28em", textTransform: "uppercase", color: statusColor(o.production_status), fontWeight: 500 }}>
                      {o.production_status.replace(/_/g, " ")}
                    </td>
                    <td className="font-sans-body py-4 px-3" style={{ fontSize: 12.5, color: "var(--mh-muted)" }}>
                      {formatDate(o.last_match_at)}
                    </td>
                    <td className="py-4 px-3">
                      <button
                        onClick={(e) => { e.stopPropagation(); downloadManifest(o.email); }}
                        className="font-sans-body"
                        style={{
                          fontSize: 10,
                          letterSpacing: "0.28em",
                          textTransform: "uppercase",
                          background: "transparent",
                          border: "1px solid var(--mh-border)",
                          padding: "8px 14px",
                          cursor: "pointer",
                          color: "var(--mh-ink)",
                          fontWeight: 500,
                        }}
                        data-testid={`admin-order-manifest-${i}`}
                      >
                        Download
                      </button>
                    </td>
                  </tr>
                  {isOpen && (
                    <tr style={{ borderBottom: "1px solid var(--mh-border-faint)", background: "var(--mh-card)" }} data-testid={`admin-order-detail-${i}`}>
                      <td colSpan={6} style={{ padding: "20px 24px 28px" }}>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                          <div>
                            <div className="font-sans-body" style={{ fontSize: 9.5, letterSpacing: "0.4em", textTransform: "uppercase", color: "var(--mh-muted)", marginBottom: 10 }}>
                              Pigment Recipe
                            </div>
                            {o.recipe?.length ? (
                              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                                <tbody>
                                  {o.recipe.map((r, idx) => (
                                    <tr key={idx} style={{ borderBottom: "1px dotted var(--mh-border-faint)" }}>
                                      <td className="font-serif-display py-2" style={{ fontSize: 13 }}>{r.pigment}</td>
                                      <td className="font-sans-body py-2 text-right" style={{ fontSize: 12, color: "var(--mh-ink-soft)" }}>
                                        {r.percent != null ? `${r.percent}%` : "—"}
                                      </td>
                                      <td className="font-sans-body py-2 text-right" style={{ fontSize: 12, color: "var(--mh-muted)" }}>
                                        {r.grams_per_50ml ? `${r.grams_per_50ml} g` : ""}
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            ) : (
                              <div className="font-sans-body" style={{ fontSize: 12, color: "var(--mh-muted)", fontStyle: "italic" }}>
                                Recipe not yet recorded.
                              </div>
                            )}
                          </div>
                          <div>
                            <div className="font-sans-body" style={{ fontSize: 9.5, letterSpacing: "0.4em", textTransform: "uppercase", color: "var(--mh-muted)", marginBottom: 10 }}>
                              Order Details
                            </div>
                            <DetailRow label="Hex" value={o.hex} />
                            <DetailRow label="Undertone" value={o.undertone || "—"} />
                            <DetailRow label="ΔE" value={o.delta_e || "—"} />
                            <DetailRow label="Customer root" value={o.root_shade || "—"} />
                            <DetailRow label="Needs Veil base" value={o.needs_atelier_system ? "Yes" : "No"} />
                            <DetailRow label="Confidence" value={o.confidence != null ? `${o.confidence}%` : "—"} />
                            <div className="font-sans-body mt-6" style={{ fontSize: 9.5, letterSpacing: "0.4em", textTransform: "uppercase", color: "var(--mh-muted)", marginBottom: 10 }}>
                              Update Status
                            </div>
                            <div className="flex flex-wrap gap-2">
                              {STATUS_OPTIONS.map((s) => (
                                <button
                                  key={s.id}
                                  onClick={(e) => { e.stopPropagation(); updateStatus(o.email, s.id); }}
                                  className="font-sans-body"
                                  style={{
                                    fontSize: 10,
                                    letterSpacing: "0.24em",
                                    textTransform: "uppercase",
                                    padding: "6px 12px",
                                    border: "1px solid var(--mh-border)",
                                    background: o.production_status === s.id ? "var(--mh-ink)" : "transparent",
                                    color: o.production_status === s.id ? "#fff" : "var(--mh-ink)",
                                    cursor: "pointer",
                                    fontWeight: 500,
                                  }}
                                  data-testid={`admin-order-status-${i}-${s.id}`}
                                >
                                  {s.label}
                                </button>
                              ))}
                            </div>
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function DetailRow({ label, value }) {
  return (
    <div className="flex justify-between py-1.5" style={{ borderBottom: "1px dotted var(--mh-border-faint)" }}>
      <span className="font-sans-body" style={{ fontSize: 11, color: "var(--mh-muted)" }}>{label}</span>
      <span className="font-sans-body" style={{ fontSize: 12, color: "var(--mh-ink)", fontWeight: 500 }}>{value}</span>
    </div>
  );
}

function StatCard({ label, value, loading }) {
  return (
    <div
      style={{
        background: "var(--mh-card)",
        border: "1px solid var(--mh-border-faint)",
        padding: "28px 26px",
      }}
      data-testid={`stat-${label.toLowerCase().replace(/\s+/g, "-")}`}
    >
      <div
        className="font-sans-body"
        style={{
          fontSize: 10,
          letterSpacing: "0.34em",
          textTransform: "uppercase",
          color: "var(--mh-muted)",
          fontWeight: 500,
        }}
      >
        {label}
      </div>
      <div
        className="font-serif-display mt-4"
        style={{
          fontSize: 56,
          lineHeight: 1,
          fontWeight: 500,
          color: "var(--mh-ink)",
        }}
      >
        {loading ? "—" : value}
      </div>
    </div>
  );
}

export default function AdminPage() {
  const [token, setToken] = useState(() => localStorage.getItem(STORAGE_KEY));

  const logout = () => {
    localStorage.removeItem(STORAGE_KEY);
    setToken(null);
  };

  if (!token) {
    return <LoginScreen onSuccess={setToken} />;
  }
  return <Dashboard token={token} onLogout={logout} />;
}
